import { createSelector } from '@reduxjs/toolkit';
import type {
  AutoConfiguratorState,
  LineItem,
  LineItemDisplayStatus,
  QuantityBreak,
} from '@snap-types/models/auto-configurator/state';

import { deriveLineItemDisplayStatus } from './helpers/line-item-display-status';
import { selectLineItemUuids } from './line-item';

export type CalculatedDataSummaryRow = {
  lineItem: LineItem;
  lineItemDisplayStatus: LineItemDisplayStatus;
  quantityBreak: QuantityBreak;
};

export const selectCalculatedDataSummaryRows = createSelector(
  [
    selectLineItemUuids,
    (state: AutoConfiguratorState) => state.lineItems,
    (state: AutoConfiguratorState) => state.quantityBreaks,
    (state: AutoConfiguratorState) =>
      state.pendingCalculationRequestIdByLineItemUuid,
    (state: AutoConfiguratorState) => state.calculationErrorsByLineItemUuid,
  ],
  (
    lineItemUuids,
    lineItemsByUuid,
    quantityBreaksByUuid,
    pendingCalculationRequestIdByLineItemUuid,
    calculationErrorsByLineItemUuid,
  ): CalculatedDataSummaryRow[] => {
    return lineItemUuids.flatMap((lineItemUuid) => {
      const lineItem = lineItemsByUuid[lineItemUuid];

      if (!lineItem) {
        return [];
      }

      const quantityBreaks = lineItem.quantityBreakUuids
        .map((uuid) => quantityBreaksByUuid[uuid])
        .filter((quantityBreak): quantityBreak is QuantityBreak =>
          Boolean(quantityBreak),
        );

      const lineItemDisplayStatus = deriveLineItemDisplayStatus(
        lineItem,
        quantityBreaks,
        Boolean(pendingCalculationRequestIdByLineItemUuid[lineItemUuid]),
        calculationErrorsByLineItemUuid[lineItemUuid] ?? null,
      );

      return quantityBreaks.map((quantityBreak) => ({
        lineItem,
        lineItemDisplayStatus,
        quantityBreak,
      }));
    });
  },
);
